import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, ChevronRight, Pencil, Plus, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useBadgeTypesQuery } from '@/client/api/badges/badges.query';
import { useUpdateBadgeTypeMutation } from '@/client/api/badges/badges.mutation';
import type { BadgeType } from '@/client/types/badges';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import BadgeTypeFormModal from '@/client/components/admin/Badge/BadgeTypeFormModal';

const truthy = (v: unknown) => v === true || v === 1 || v === '1';

const BadgeTypesPage = () => {
  useBreadcrumbs([
    { label: 'Badges', href: '/admin/badges' },
    { label: 'Badge types' },
  ]);

  const { data: types = [], isLoading, isError } = useBadgeTypesQuery(false);
  const updateMutation = useUpdateBadgeTypeMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BadgeType | null>(null);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (type: BadgeType) => {
    setEditing(type);
    setModalOpen(true);
  };

  const toggleActive = async (type: BadgeType) => {
    const nextActive = truthy(type.active) ? 0 : 1;
    const toastId = toast.loading(nextActive ? 'Activating…' : 'Disabling…');
    try {
      await updateMutation.mutateAsync({ btid: type.btid, active: nextActive });
      toast.success(nextActive ? 'Badge type activated' : 'Badge type disabled', {
        id: toastId,
      });
    } catch (error: any) {
      toast.error('Update failed', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
              <Award className="h-6 w-6 text-amber-600" />
              Badge types
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Click a row or <span className="font-medium text-gray-700">Edit type</span> to
              change settings and auto-award rules.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/admin/badges?status=under_review">Review queue</Link>
            </Button>
            <Button className="gap-1.5" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              New type
            </Button>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Name', 'Category', 'Tier', 'Scope', 'Method', 'Status', 'Actions'].map(
                  (heading) => (
                    <th
                      key={heading}
                      className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${
                        heading === 'Actions' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {heading}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading &&
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index}>
                    {Array.from({ length: 7 }).map((__, cell) => (
                      <td key={cell} className="px-6 py-4">
                        <Skeleton className="h-4 w-20" />
                      </td>
                    ))}
                  </tr>
                ))}

              {!isLoading && isError && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-red-600">
                    Could not load badge types.
                  </td>
                </tr>
              )}

              {!isLoading && !isError && types.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-gray-500">
                    No badge types yet.{' '}
                    <button
                      type="button"
                      className="font-medium text-blue-600 hover:underline"
                      onClick={openCreate}
                    >
                      Create one
                    </button>{' '}
                    to get started.
                  </td>
                </tr>
              )}

              {types.map((type) => {
                const active = truthy(type.active);
                return (
                  <tr
                    key={type.btid}
                    className="cursor-pointer hover:bg-amber-50/60"
                    onClick={() => openEdit(type)}
                  >
                    <td className="px-6 py-4">
                      <span className="block text-sm font-medium text-blue-700 hover:underline">
                        {type.name}
                      </span>
                      <span className="block text-xs text-gray-500">
                        btid {type.btid}
                        {type.name_system ? ` · ${type.name_system}` : ''}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                      {type.category || '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                      {type.tier ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                      {type.scope || '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                      {type.award_method || '—'}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {active ? 'Active' : 'Inactive'}
                      </span>
                      {truthy(type.requires_verification) ? (
                        <span className="ml-1 inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                          verified
                        </span>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <div
                        className="flex justify-end gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          size="sm"
                          className="gap-1.5"
                          onClick={() => openEdit(type)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit type
                          <ChevronRight className="h-3.5 w-3.5 opacity-70" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className={active ? 'text-red-600' : 'text-green-700'}
                          disabled={updateMutation.isPending}
                          onClick={() => void toggleActive(type)}
                          title={active ? 'Disable' : 'Activate'}
                        >
                          {active ? (
                            <>
                              <ShieldOff className="mr-1 h-3.5 w-3.5" />
                              Disable
                            </>
                          ) : (
                            'Activate'
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <BadgeTypeFormModal
        isOpen={modalOpen}
        badgeType={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
      />
    </div>
  );
};

export default BadgeTypesPage;
